import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Users, Upload, CheckCircle, AlertCircle, X, UserCheck, UserX } from "lucide-react";
import { StatCard } from "./components/PageCards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TeamPage = ({ Layout }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team/all`);
      setMembers(res.data);
    } catch (err) {
      console.error("Failed to fetch team:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/team/import`, formData);
      setMessage({ type: "success", text: res.data.message });
      fetchMembers();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleToggleActive = async (memberId) => {
    setToggling(memberId);
    try {
      await axios.patch(`${API}/team/${memberId}/toggle-active`);
      fetchMembers();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed" });
    } finally {
      setToggling(null);
    }
  };

  const users = useMemo(() => members.filter(m => m.member_type === "office"), [members]);
  const inspectors = useMemo(() => members.filter(m => m.member_type === "inspector"), [members]);
  const activeUsers = useMemo(() => users.filter(m => m.active === "yes"), [users]);
  const activeInspectors = useMemo(() => inspectors.filter(m => m.active === "yes"), [inspectors]);

  const displayList = activeTab === "users" ? users : inspectors;

  const userColumns = [
    { key: "login_id", label: "Login ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "department", label: "Department" },
    { key: "role", label: "Role" },
    { key: "active", label: "Active" },
  ];

  const inspectorColumns = [
    { key: "login_id", label: "Inspector ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "region", label: "Region" },
    { key: "active", label: "Active" },
  ];

  const columns = activeTab === "users" ? userColumns : inspectorColumns;

  return (
    <Layout>
      <div className="space-y-6" data-testid="team-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Team Management</h1>
            <p className="text-gray-500 text-sm">{displayList.length} {activeTab === "users" ? "office users" : "inspectors"}</p>
          </div>
          <label className={`px-3 py-1.5 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] text-sm font-semibold rounded-lg flex items-center gap-1.5 cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="upload-team-btn">
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading..." : "Upload Spreadsheet"}
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>

        {message && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
            {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span className="text-sm">{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-cards">
          <StatCard title="Office Users" icon={Users} color="blue" value={users.length} active={activeTab === "users"} onClick={() => setActiveTab("users")} testId="team-card-users" />
          <StatCard title="Active Users" icon={UserCheck} color="emerald" value={activeUsers.length} active={false} onClick={() => setActiveTab("users")} testId="team-card-active-users" />
          <StatCard title="Inspectors" icon={Users} color="amber" value={inspectors.length} active={activeTab === "inspectors"} onClick={() => setActiveTab("inspectors")} testId="team-card-inspectors" />
          <StatCard title="Active Inspectors" icon={UserCheck} color="cyan" value={activeInspectors.length} active={false} onClick={() => setActiveTab("inspectors")} testId="team-card-active-inspectors" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" />
            </div>
          ) : displayList.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No {activeTab === "users" ? "office users" : "inspectors"} found. Upload a spreadsheet to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    {columns.map(col => (
                      <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col.label}</th>
                    ))}
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayList.map(member => (
                    <tr key={member.id} className="hover:bg-white/80 transition-colors" data-testid={`team-row-${member.login_id}`}>
                      {columns.map(col => (
                        <td key={col.key} className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {col.key === "active" ? (
                            member.active === "yes" ? (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#A7C4A0]/25 text-[#6B8F62]">Active</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#C07070]/20 text-[#8B4545]">Inactive</span>
                            )
                          ) : (
                            member[col.key] || "-"
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(member.id)}
                          disabled={toggling === member.id}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                            member.active === "yes"
                              ? "bg-[#C07070]/20 text-[#8B4545] hover:bg-[#C07070]/25"
                              : "bg-[#A7C4A0]/25 text-[#6B8F62] hover:bg-[#A7C4A0]/30"
                          }`}
                          data-testid={`toggle-active-${member.login_id}`}
                        >
                          {toggling === member.id ? "..." : member.active === "yes" ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TeamPage;
